import { useState } from "react";
import { ApiError } from "@/api/client";
import { roleLabel, useSession } from "@/api/session";
import {
  useAddHoliday,
  useAdminUsers,
  useCreateTeam,
  useCreateUser,
  useDeleteHoliday,
  useHolidays,
  useResetUserPassword,
  useSetUserGender,
  useTeams,
  useUpdateUser,
} from "@/api/queries";
import type { AdminUser, EmploymentType, Gender, Role, Team, WorkMode } from "@/api/types";
import { PageHeader } from "@/app/page-header";
import { StatTile } from "@/components/charts";
import { EligibilityPreview, LeavePolicyPanel } from "@/components/leave-policy";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import { Field, Input, LabelledInput, Select } from "@/components/ui/field";
import { Icon } from "@/components/ui/icon";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { EmptyState, ErrorState, SkeletonTable } from "@/components/ui/states";
import { TBody, TD, TDPrimary, TH, THead, TR, TableWrap } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/ui/toast";
import { todayStr } from "@/lib/calendar";
import { formatDate } from "@/lib/format";
import { EMPLOYMENT_TYPE_LABEL, WORK_MODE_LABEL } from "@/lib/labels";

/** What the one-time-password dialog needs to know: who it's for, and whether
 *  it came from creating the account or resetting an existing one — the copy
 *  around the password differs, the password's meaning doesn't. */
interface IssuedPassword {
  kind: "created" | "reset";
  name: string;
  email: string;
  password: string;
}

export function HrAccountsPage() {
  const users = useAdminUsers();
  const teams = useTeams();
  const me = useSession((s) => s.user);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [resetting, setResetting] = useState<AdminUser | null>(null);
  const [issued, setIssued] = useState<IssuedPassword | null>(null);

  // Everyone with an account, HR & Admin included — they are employees of the
  // company too, and leaving them out of their own directory made Mentoria's HRBP
  // invisible on every screen that counts people.
  const staff = users.data ?? [];
  const pending = staff.filter((u) => u.must_reset_password);

  if (users.error) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader eyebrow="Admin Account only" title="User Accounts" />
        <Panel>
          <ErrorState message={users.error.message} onRetry={() => void users.refetch()} />
        </Panel>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Admin Account only"
        title="User Accounts"
        sub="Create and maintain Manager and Employee accounts, mapped to a team where relevant. These rules mirror the backend exactly."
        actions={
          <Button variant="primary" iconLeft="add-person" onClick={() => setCreating(true)}>
            Create account
          </Button>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile label="Accounts" value={staff.length} icon="user-accounts" loading={users.isLoading} />
        <StatTile
          label="Managers"
          value={staff.filter((u) => u.role === "manager").length}
          icon="team"
          loading={users.isLoading}
        />
        <StatTile
          label="Yet to set a password"
          value={pending.length}
          icon="lock"
          loading={users.isLoading}
          meta={pending.length === 0 ? "Everyone is set up" : "Still on a one-time password"}
        />
        <StatTile label="Teams available" value={teams.data?.length ?? 0} icon="org" loading={teams.isLoading} />
      </div>

      <Panel>
        <PanelHeader title="All accounts" note="Newest first" />
        {users.isLoading ? (
          <SkeletonTable rows={8} cols={6} />
        ) : staff.length === 0 ? (
          <EmptyState
            icon="user-accounts"
            title="No accounts yet"
            body="Create the first Manager or Employee account. They sign in with a one-time password and are then asked to set their own and complete onboarding."
            action={
              <Button variant="primary" iconLeft="add-person" onClick={() => setCreating(true)}>
                Create account
              </Button>
            }
          />
        ) : (
          <TableWrap>
            <THead>
              <TH>Person</TH>
              <TH>Role</TH>
              <TH>Team</TH>
              <TH>Type</TH>
              <TH>Gender</TH>
              <TH>Work mode</TH>
              <TH>Status</TH>
              <TH>
                <span className="sr-only">Actions</span>
              </TH>
            </THead>
            <TBody>
              {staff.map((account) => (
                <TR key={account.id}>
                  <TDPrimary>
                    <span className="flex items-center gap-2">
                      <Avatar name={account.name} size="sm" />
                      <span className="min-w-0">
                        <span className="block truncate">{account.name ?? "Unnamed"}</span>
                        <span className="block truncate font-mono text-xs font-normal text-muted">
                          {account.email}
                        </span>
                      </span>
                    </span>
                  </TDPrimary>
                  <TD>{roleLabel(account)}</TD>
                  <TD>{account.team_name ?? "Unassigned"}</TD>
                  <TD>{EMPLOYMENT_TYPE_LABEL[account.employment_type]}</TD>
                  <TD>
                    {account.gender ? (
                      account.gender
                    ) : (
                      /* Unset gender is not cosmetic: the Menstrual Leave rule
                         fails open, so this account still sees the benefit.
                         Flagged rather than shown as a blank cell. */
                      <Badge tone="warn" icon="warning-circle">
                        Not set
                      </Badge>
                    )}
                  </TD>
                  <TD>{WORK_MODE_LABEL[account.work_mode]}</TD>
                  <TD>
                    {account.must_reset_password ? (
                      <Badge tone="warn" icon="lock">
                        First login pending
                      </Badge>
                    ) : (
                      <Badge tone="good" icon="check-circle">
                        Active
                      </Badge>
                    )}
                  </TD>
                  <TD>
                    <span className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" iconLeft="edit" onClick={() => setEditing(account)}>
                        Edit
                      </Button>
                      {/* Not on your own row — the backend rejects self-reset
                          (that's what Account Settings > change password is for). */}
                      {account.id !== me?.id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          iconLeft="password"
                          onClick={() => setResetting(account)}
                        >
                          Reset password
                        </Button>
                      )}
                    </span>
                  </TD>
                </TR>
              ))}
            </TBody>
          </TableWrap>
        )}
      </Panel>

      <LeavePolicyPanel
        title="What each account becomes eligible for"
        note="Employment type and gender on an account decide this. The backend applies these rules on every request; this table is here so the reason is visible."
      />

      <HolidayManager />

      {creating && (
        <AccountDialog
          mode="create"
          teams={teams.data ?? []}
          onClose={() => setCreating(false)}
          onIssued={setIssued}
        />
      )}
      {editing && (
        <AccountDialog
          mode="edit"
          account={editing}
          teams={teams.data ?? []}
          onClose={() => setEditing(null)}
        />
      )}
      {resetting && (
        <ResetPasswordDialog
          account={resetting}
          onClose={() => setResetting(null)}
          onIssued={setIssued}
        />
      )}
      {issued && <OneTimePasswordDialog issued={issued} onClose={() => setIssued(null)} />}
    </div>
  );
}

/**
 * Create and edit share one form: the backend takes the same field set for both,
 * and keeping them together means the validation rules can't drift apart.
 */
function AccountDialog({
  mode,
  account,
  teams,
  onClose,
  onIssued,
}: {
  mode: "create" | "edit";
  account?: AdminUser;
  teams: { id: number; name: string }[];
  onClose: () => void;
  onIssued?: (issued: IssuedPassword) => void;
}) {
  const create = useCreateUser();
  const update = useUpdateUser();
  const setGender = useSetUserGender();

  const [name, setName] = useState(account?.name ?? "");
  const [email, setEmail] = useState(account?.email ?? "");
  const [designation, setDesignation] = useState(account?.designation ?? "");
  const [role, setRole] = useState<Role>(account?.role ?? "employee");
  const [teamId, setTeamId] = useState<string>(account?.team_id ? String(account.team_id) : "");
  const [addingTeam, setAddingTeam] = useState(false);
  // Holds a just-created team until the refetched `teams` list includes it, so
  // the Select has something to show as selected in the meantime.
  const [pendingTeam, setPendingTeam] = useState<Team | null>(null);
  /*
    The options this Select can show.

    Two teams can be missing from `teams`: one just created (held in
    `pendingTeam` until the refetch catches up), and the account's OWN team when
    it is not assignable — GET /admin/teams filters out "HR", because HR is an
    internal function rather than a team you post someone to. Without the second
    case, editing anyone on the HR team showed "Not mapped" against a row that
    says HR, and saving posted teamId: null and wiped it.
  */
  const teamOptions = (() => {
    let options = teams;
    if (pendingTeam && !options.some((t) => t.id === pendingTeam.id)) options = [...options, pendingTeam];
    if (
      account?.team_id &&
      account.team_name &&
      !options.some((t) => t.id === account.team_id)
    ) {
      options = [...options, { id: account.team_id, name: account.team_name }];
    }
    return options;
  })();
  const ADD_TEAM_VALUE = "__add_new_team__";
  const [workMode, setWorkMode] = useState<WorkMode>(account?.work_mode ?? "onsite");
  const [employmentType, setEmploymentType] = useState<EmploymentType>(
    account?.employment_type ?? "FTE",
  );
  const [gender, setGenderValue] = useState<Gender | "">(account?.gender ?? "");
  const [isPeopleFunctionApprover, setIsPeopleFunctionApprover] = useState(
    account?.is_people_function_approver ?? false,
  );
  const [error, setError] = useState<string | null>(null);

  // An existing HR & Admin account's role can't be changed here — this form
  // only offers Employee and Manager for edits, so letting it post a role for
  // an existing admin would read as "about to demote them", and the backend
  // ignores the submitted role for admins for the same reason. Creating a new
  // admin account is a different action and is allowed below.
  const editingAdmin = account?.role === "admin";
  const isAdminRole = editingAdmin || role === "admin";

  const busy = create.isPending || update.isPending;

  async function onSubmit() {
    setError(null);
    if (!name.trim() || !email.trim() || !designation.trim()) {
      setError("Name, email and designation are all required.");
      return;
    }
    if (!editingAdmin && role === "employee" && !teamId) {
      setError("An employee must be mapped to a team.");
      return;
    }

    try {
      if (mode === "create") {
        const result = await create.mutateAsync({
          email: email.trim(),
          name: name.trim(),
          designation: designation.trim(),
          role,
          teamId: teamId ? Number(teamId) : null,
          workMode,
          employmentType,
          ...(gender ? { gender } : {}),
          ...(role === "admin" ? { isPeopleFunctionApprover } : {}),
        });
        onIssued?.({
          kind: "created",
          name: result.user.name ?? result.user.email,
          email: result.user.email,
          password: result.oneTimePassword,
        });
      } else if (account) {
        await update.mutateAsync({
          id: account.id,
          email: email.trim(),
          name: name.trim(),
          designation: designation.trim(),
          role,
          teamId: teamId ? Number(teamId) : null,
          workMode,
          employmentType,
          ...(editingAdmin ? { isPeopleFunctionApprover } : {}),
        });
        if (gender && gender !== account.gender) {
          await setGender.mutateAsync({ id: account.id, gender });
        }
        toast.success("Account updated.");
      }
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save this account.");
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent wide>
        <DialogHeader
          title={mode === "create" ? "Create an account" : "Edit account"}
          description={
            mode === "create"
              ? "A one-time password is generated once and shown only after you save. It is never stored in plain text and cannot be retrieved again."
              : "Role and team changes reach an open session the next time that person signs in."
          }
        />
        <DialogBody>
          {error && (
            <p
              role="alert"
              className="mb-4 flex items-start gap-2 rounded-lg bg-bad-wash px-3 py-2.5 text-[13px] leading-relaxed text-bad"
            >
              <Icon name="warning-circle" size={16} className="mt-0.5" />
              <span>{error}</span>
            </p>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <LabelledInput
              label="Full name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <LabelledInput
              label="Work email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <LabelledInput
              label="Designation"
              required
              value={designation}
              onChange={(e) => setDesignation(e.target.value)}
            />
            <Field
              label="Role"
              htmlFor="acc-role"
              required={!editingAdmin}
              hint={editingAdmin ? "An Admin Account's role can't be changed here." : undefined}
            >
              <Select
                id="acc-role"
                value={editingAdmin ? "admin" : role}
                disabled={editingAdmin}
                onChange={(e) => setRole(e.target.value as Role)}
              >
                {editingAdmin && <option value="admin">HR &amp; Admin</option>}
                <option value="employee">Employee</option>
                <option value="manager">Manager</option>
                {mode === "create" && <option value="admin">HR &amp; Admin</option>}
              </Select>
            </Field>
            <Field
              label="Team"
              htmlFor="acc-team"
              required={!editingAdmin && role === "employee"}
              hint={
                isAdminRole
                  ? "Admin Account sits across the organisation rather than in one team."
                  : role === "manager"
                    ? "Optional, but a manager only sees a team once mapped."
                    : undefined
              }
            >
              <Select
                id="acc-team"
                value={teamId}
                onChange={(e) => {
                  if (e.target.value === ADD_TEAM_VALUE) {
                    setAddingTeam(true);
                    return;
                  }
                  setTeamId(e.target.value);
                }}
              >
                <option value="">Not mapped</option>
                {teamOptions.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
                <option value={ADD_TEAM_VALUE}>+ Add new team…</option>
              </Select>
            </Field>
            <Field label="Work mode" htmlFor="acc-mode" required>
              <Select
                id="acc-mode"
                value={workMode}
                onChange={(e) => setWorkMode(e.target.value as WorkMode)}
              >
                <option value="onsite">Onsite</option>
                <option value="hybrid">Hybrid</option>
                <option value="remote">Remote</option>
              </Select>
            </Field>
            <Field
              label="Employment type"
              htmlFor="acc-type"
              required
              hint="Interns are not eligible for Earned, Special or Optional Holiday leave."
            >
              <Select
                id="acc-type"
                value={employmentType}
                onChange={(e) => setEmploymentType(e.target.value as EmploymentType)}
              >
                <option value="FTE">Full-time</option>
                <option value="Intern">Intern</option>
              </Select>
            </Field>
            <Field
              label="Gender"
              htmlFor="acc-gender"
              hint="Drives Menstrual Leave eligibility. If left blank, the person is asked during their own setup."
            >
              <Select
                id="acc-gender"
                value={gender}
                onChange={(e) => setGenderValue(e.target.value as Gender)}
              >
                <option value="">Not set</option>
                <option value="Female">Female</option>
                <option value="Male">Male</option>
                <option value="Other">Other</option>
              </Select>
            </Field>
          </div>

          {isAdminRole && (
            <label className="mt-4 flex items-start gap-2.5 rounded-lg border border-line bg-surface-sunken px-3 py-2.5 text-[13px] text-ink">
              <input
                type="checkbox"
                className="mt-0.5 size-4 shrink-0 accent-accent"
                checked={isPeopleFunctionApprover}
                onChange={(e) => setIsPeopleFunctionApprover(e.target.checked)}
              />
              <span>
                <span className="block font-medium">People-Function approver</span>
                <span className="block text-xs leading-relaxed text-muted">
                  Can decide leave requests from managers, and from the primary HR &amp; Admin
                  account. Only eligible admin accounts can approve or reject a manager&apos;s or
                  Vyomi&apos;s leave.
                </span>
              </span>
            </label>
          )}

          {/* Employment type and gender both change what this person can apply
              for, so the consequence is shown before the form is saved. */}
          <EligibilityPreview className="mt-4" employmentType={employmentType} gender={gender} />
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSubmit} loading={busy}>
            {mode === "create" ? "Create account" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>

      {addingTeam && (
        <AddTeamDialog
          onClose={() => setAddingTeam(false)}
          onCreated={(team) => {
            setPendingTeam(team);
            setTeamId(String(team.id));
            setAddingTeam(false);
          }}
        />
      )}
    </Dialog>
  );
}

/** Quick "add a team" flow reachable from the Team dropdown above, so creating
 *  a team doesn't require leaving the create-account form. Teams have no
 *  fields beyond a name. */
function AddTeamDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (team: Team) => void;
}) {
  const createTeam = useCreateTeam();
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function onSubmit() {
    setError(null);
    if (!name.trim()) {
      setError("Give the team a name.");
      return;
    }
    try {
      const result = await createTeam.mutateAsync(name.trim());
      toast.success("Team created.");
      onCreated(result.team);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not create that team.");
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader
          title="Add a new team"
          description="It'll appear in the Team dropdown immediately, ready to assign."
        />
        <DialogBody>
          {error && (
            <p
              role="alert"
              className="mb-4 flex items-start gap-2 rounded-lg bg-bad-wash px-3 py-2.5 text-[13px] leading-relaxed text-bad"
            >
              <Icon name="warning-circle" size={16} className="mt-0.5" />
              <span>{error}</span>
            </p>
          )}
          <LabelledInput
            label="Team name"
            required
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={createTeam.isPending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSubmit} loading={createTeam.isPending}>
            Add team
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Confirmation before resetting someone's password. The consequence — their
 * current password stops working the moment this succeeds — is stated up front,
 * because the person being helped may be mid-session somewhere.
 */
function ResetPasswordDialog({
  account,
  onClose,
  onIssued,
}: {
  account: AdminUser;
  onClose: () => void;
  onIssued: (issued: IssuedPassword) => void;
}) {
  const reset = useResetUserPassword();
  const [error, setError] = useState<string | null>(null);

  async function onConfirm() {
    setError(null);
    try {
      const result = await reset.mutateAsync(account.id);
      onIssued({
        kind: "reset",
        name: result.user.name ?? result.user.email,
        email: result.user.email,
        password: result.oneTimePassword,
      });
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not reset this password.");
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader
          title="Reset password?"
          description={`A new temporary password will be generated for ${account.name ?? account.email}. Their current password stops working immediately, and they'll be asked to set their own password on their next sign-in.`}
        />
        <DialogBody className="flex flex-col gap-4">
          {error && (
            <p
              role="alert"
              className="flex items-start gap-2 rounded-lg bg-bad-wash px-3 py-2.5 text-[13px] leading-relaxed text-bad"
            >
              <Icon name="warning-circle" size={16} className="mt-0.5" />
              <span>{error}</span>
            </p>
          )}
          <div className="rounded-lg border border-line bg-surface-sunken p-4">
            <p className="text-[13px] font-medium text-ink">{account.name ?? "Unnamed"}</p>
            <p className="font-mono text-xs text-muted">{account.email}</p>
          </div>
          <p className="flex items-start gap-2 text-xs leading-relaxed text-muted">
            <Icon name="info" size={16} className="mt-px shrink-0 text-faint" />
            <span>
              The temporary password is shown only once, right after this — pass it on through a
              secure channel. Nothing about their account or data changes besides the password.
            </span>
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={reset.isPending}>
            Cancel
          </Button>
          <Button variant="danger" iconLeft="password" onClick={onConfirm} loading={reset.isPending}>
            Reset password
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Shown once. The backend never stores or returns this password again. */
function OneTimePasswordDialog({
  issued,
  onClose,
}: {
  issued: IssuedPassword;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader
          title={issued.kind === "created" ? "Account created" : "Password reset"}
          description={
            issued.kind === "created"
              ? "Pass this one-time password to the new joiner through a secure channel. It is shown only now and cannot be retrieved again."
              : "Pass this temporary password on through a secure channel. It is shown only now and cannot be retrieved again."
          }
        />
        <DialogBody className="flex flex-col gap-4">
          <div className="rounded-lg border border-line bg-surface-sunken p-4">
            <p className="text-[13px] font-medium text-ink">{issued.name}</p>
            <p className="font-mono text-xs text-muted">{issued.email}</p>
            <div className="mt-3 flex items-center gap-2">
              <code className="flex-1 rounded-lg border border-line bg-surface px-3 py-2 font-mono text-base tracking-wide text-ink">
                {issued.password}
              </code>
              <Button
                variant="secondary"
                size="icon"
                aria-label="Copy one-time password"
                onClick={() => {
                  void navigator.clipboard.writeText(issued.password);
                  setCopied(true);
                  toast.success("Copied to clipboard.");
                }}
              >
                <Icon name={copied ? "check" : "copy"} size={16} />
              </Button>
            </div>
          </div>
          <p className="flex items-start gap-2 text-xs leading-relaxed text-muted">
            <Icon name="info" size={16} className="mt-px shrink-0 text-faint" />
            <span>
              {issued.kind === "created"
                ? "They will be asked to set their own password and complete account setup before anything else becomes reachable."
                : "They sign in with this temporary password and are asked to choose their own new password before anything else becomes reachable."}
            </span>
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Holiday administration lives here because only HR and Admin can write it. */
function HolidayManager() {
  const holidays = useHolidays();
  const addHoliday = useAddHoliday();
  const deleteHoliday = useDeleteHoliday();
  const [date, setDate] = useState(todayStr());
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const fixed = (holidays.data ?? []).filter((h) => h.category !== "optional");
  const optional = (holidays.data ?? []).filter((h) => h.category === "optional");

  async function onAdd() {
    setError(null);
    if (!name.trim()) {
      setError("Give the holiday a name.");
      return;
    }
    try {
      await addHoliday.mutateAsync({ date, name: name.trim() });
      setName("");
      toast.success("Holiday added.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not add that holiday.");
    }
  }

  return (
    <Panel>
      <PanelHeader
        title="Company holidays"
        note="Fixed holidays drive the org-wide weekly-off and comp-off rules. Optional holidays are applied for individually, up to two a year."
      />
      <div className="border-b border-line px-4 py-3">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Date" htmlFor="hol-date" className="w-40">
            <Input id="hol-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
          <Field label="Holiday name" htmlFor="hol-name" className="min-w-52 flex-1">
            <Input
              id="hol-name"
              placeholder="Independence Day"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <Button variant="primary" iconLeft="plus" onClick={onAdd} loading={addHoliday.isPending}>
            Add holiday
          </Button>
        </div>
        {error && (
          <p role="alert" className="mt-2 flex items-start gap-1.5 text-xs text-bad">
            <Icon name="warning-circle" size={16} />
            <span>{error}</span>
          </p>
        )}
      </div>

      <Tabs defaultValue="fixed">
        <div className="border-b border-line px-4 py-2.5">
          <TabsList>
            <TabsTrigger value="fixed">Fixed ({fixed.length})</TabsTrigger>
            <TabsTrigger value="optional">Optional ({optional.length})</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="fixed">
          <HolidayTable
            holidays={fixed}
            loading={holidays.isLoading}
            onDelete={(id) =>
              deleteHoliday.mutate(id, {
                onSuccess: () => toast.success("Holiday removed."),
                onError: (err) =>
                  toast.error(err instanceof ApiError ? err.message : "Could not remove that."),
              })
            }
          />
        </TabsContent>
        <TabsContent value="optional">
          <HolidayTable
            holidays={optional}
            loading={holidays.isLoading}
            onDelete={(id) =>
              deleteHoliday.mutate(id, {
                onSuccess: () => toast.success("Holiday removed."),
                onError: (err) =>
                  toast.error(err instanceof ApiError ? err.message : "Could not remove that."),
              })
            }
          />
        </TabsContent>
      </Tabs>
    </Panel>
  );
}

function HolidayTable({
  holidays,
  loading,
  onDelete,
}: {
  holidays: { id?: number; date: string; name: string }[];
  loading: boolean;
  onDelete: (id: number) => void;
}) {
  if (loading) return <SkeletonTable rows={5} cols={3} />;
  if (holidays.length === 0) return <EmptyState icon="holiday" title="No holidays in this list" />;
  return (
    <div className="max-h-96 overflow-y-auto">
      <TableWrap>
        <THead>
          <TH>Date</TH>
          <TH>Holiday</TH>
          <TH>
            <span className="sr-only">Remove</span>
          </TH>
        </THead>
        <TBody>
          {holidays.map((holiday) => (
            <TR key={holiday.date}>
              <TDPrimary className="whitespace-nowrap font-mono text-xs">
                {formatDate(holiday.date)}
              </TDPrimary>
              <TD>{holiday.name}</TD>
              <TD>
                {holiday.id !== undefined && (
                  <Button
                    variant="ghost"
                    size="sm"
                    iconLeft="trash"
                    onClick={() => onDelete(holiday.id as number)}
                    aria-label={`Remove ${holiday.name}`}
                  >
                    Remove
                  </Button>
                )}
              </TD>
            </TR>
          ))}
        </TBody>
      </TableWrap>
    </div>
  );
}
